import {
  Component,
  ElementRef,
  Injector,
  InputSignal,
  signal,
  Type,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { ModalService } from '../../state/modal/modal.service';
import { NgClass } from '@angular/common';
import { AnalyticsService } from '../../state/analytics/analytics.service';
import { ConfirmComponent } from '../confirm/confirm.component';
import { customEventSeparator as seperator } from '@shared/utils/analytics';
import { FeatureType } from '@shared/interfaces/feedback';

type ConfirmModalContext = InputSignal<'feedback' | 'user'>;
export type ModalAnimation = 'tada' | 'slideInOut';

@Component({
  selector: 'app-modal',
  imports: [NgClass],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.css',
})
export class ModalComponent {
  private injector = inject(Injector);
  private modalService = inject(ModalService);
  private analytics = inject(AnalyticsService);

  @ViewChild('modalContent', { read: ViewContainerRef, static: true }) modalContent!: ViewContainerRef;
  @ViewChild('modal', { static: true }) modalElement!: ElementRef;
  private readonly animationState = signal<ModalAnimation>('tada');
  private readonly cancellableState = signal(true);
  private closing = false;
  private closeParentAfterClose = false;
  private closeTimeout?: ReturnType<typeof setTimeout>;
  public get animation(): ModalAnimation {
    return this.animationState();
  }
  public set animation(value: ModalAnimation) {
    this.animationState.set(value);
  }
  public get cancellable(): boolean {
    return this.cancellableState();
  }
  public set cancellable(value: boolean) {
    this.cancellableState.set(value);
  }
  public trackOnClose?: string;
  public confirmModalClose = false;
  private componentInstance?: object;
  private compData?: Partial<object & { feature?: () => FeatureType }>;

  public insertComponent<T extends object>(component: Type<T>, data?: Partial<T>) {
    const componentRef = this.modalContent.createComponent(component, { injector: this.injector });
    this.componentInstance = componentRef.instance;

    if (data) {
      this.compData = data;
      Object.assign(componentRef.instance, data);
    }

    // Listen to output events if the component is ConfirmComponent
    if (component === ConfirmComponent) {
      (componentRef.instance as ConfirmComponent).confirmAction.subscribe(() => this.handleConfirm());
      (componentRef.instance as ConfirmComponent).cancelAction.subscribe(() => this.handleCancel());
    }
  }

  public close() {
    const modalElement = this.modalElement.nativeElement as HTMLElement;
    if (this.closing) return;

    this.closing = true;
    modalElement.addEventListener('animationend', this.handleCloseAnimationEnd);
    modalElement.classList.add('modal-close');
    this.closeTimeout = setTimeout(() => this.finishClose(), 200);
  }

  private readonly handleCloseAnimationEnd = (event: AnimationEvent) => {
    if (event.animationName !== 'modal-close') return;
    this.finishClose();
  };

  private finishClose() {
    if (!this.closing) return;

    this.closing = false;
    clearTimeout(this.closeTimeout);
    this.closeTimeout = undefined;
    const modalElement = this.modalElement.nativeElement as HTMLElement;
    modalElement.removeEventListener('animationend', this.handleCloseAnimationEnd);
    this.modalService._closeModal();
    if (this.closeParentAfterClose) {
      this.closeParentAfterClose = false;
      this.modalService.closeModal();
    }
  }

  private handleConfirm() {
    this.closeParentAfterClose = true;
    this.close();
    this.track();
  }

  private handleCancel() {
    this.modalService.closeModal();
  }

  public cancel() {
    if (!this.cancellable) return;
    if (this.shouldConfirm()) {
      // If confirm is requested from feature, use feature name in tracking else it is from update user modal
      const trackOnClose = this.compData?.feature ? `${this.compData.feature()}${seperator}feedback_cancel` : undefined;
      const c = signal(trackOnClose ? 'feedback' : 'user') as unknown as ConfirmModalContext;
      this.modalService.openModal(ConfirmComponent, { data: { context: c }, cancellable: false, trackOnClose });
    } else {
      this.close();
      this.track();
    }
  }

  private shouldConfirm() {
    if (
      this.componentInstance &&
      'shouldConfirmModalClose' in this.componentInstance &&
      typeof this.componentInstance.shouldConfirmModalClose === 'function'
    ) {
      return this.confirmModalClose && this.componentInstance.shouldConfirmModalClose();
    }
    return this.confirmModalClose;
  }

  private track() {
    if (this.trackOnClose) {
      if (this.trackOnClose.includes(seperator)) {
        const [feature, event] = this.trackOnClose.split(seperator);
        this.analytics.trackEvent(feature as FeatureType, event);
      } else {
        this.analytics.trackEvent('feature-not-specified', this.trackOnClose);
      }
    }
  }
}
